<template>
    <div class="univstudy__filter__dropdown" :class="{ 'has-selections': hasSelections, 'is-open': isDropdownOpen }">
        <button class="general-button" @click="toggleDropdown" ref="dropdownButton">
            <span>
                {{ label }}
                <span class="count-number" v-if="hasSelections && isCheckbox">
                    {{ selectedValueDisplay }}
                </span>
            </span>
            <span class="ico-arrow" v-html="ico_dropdown_arrow" />
        </button>
        <div v-if="isDropdownOpen" class="univstudy__filter__dropdown__content__holder" ref="dropdownContent">
            
            <div class="univstudy__search__holder" v-if="hasSearch">
                <label for="search-options">
                    <span class="vh"> search</span>
                    <input
                        id="search-options"
                        :value="searchQuery"
                        @input="debouncedSearch($event.target.value)"
                        type="text"
                        :placeholder="label.toLowerCase()"
                        class="search-input"
                    />
                    <span class="vh"> search </span>
                    <span class="ico-search" v-html="ico_search"/>
                </label>
            </div>

            <div class="univstudy__filter__dropdown__content">
                <DynamicScroller
                    class="scroller"
                    :items="filteredOptions"
                    :min-item-size="52"
                    key-field="id"
                >
                    <template v-slot="{ item: option, index, active }">
                        <DynamicScrollerItem
                            :item="option"
                            :active="active"
                            :data-index="index"
                            class="scroller-item"
                        >
                            <div class="univstudy__filter" :class="isCheckbox ? 'checkbox' : 'radio'">
                                <label class="paragraph--teaser" :class="option.count == 0 ? 'zero-count' : '' ">
                                    <div class="univstudy__filter__box__holder">
                                        <input
                                            v-if="isCheckbox"
                                            type="checkbox"
                                            :value="option.id"
                                            :checked="modelValue.includes(option.id)"
                                            @change="updateModelValue(option.id, $event.target.checked)"
                                        />
                                        <input
                                            v-else
                                            type="radio"
                                            :name="radioName"
                                            :value="option.id"
                                            :checked="modelValue === option.id"
                                            @change="updateModelValue(option.id)"
                                        />
                                        <span class="univstudy__filter__box"></span>
                                    </div>
                                    <p class="paragraph--teaser">
                                        {{ option.name }}
                                        <span v-if="option.count > 0">
                                            ({{ option.count }})
                                        </span>
                                        <span v-else>
                                            (0)
                                        </span>
                                    </p>
                                </label>
                            </div>
                        </DynamicScrollerItem>
                    </template>
                </DynamicScroller>
            </div>
            <div class="univstudy__filter__dropdown__bottom">
                <button class="general-button background-black" @click="applyFilters">
                    {{ printString('apply_now') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { debounce } from 'lodash-es';

export default {
    components: {
        DynamicScroller,
        DynamicScrollerItem
    },
    props: {
        label: String,
        options: Array,
        modelValue: [Array, String], // Accept both Array and String
        selectedValue: [String, Array], // For radio or checkbox
        hasSearch: Boolean,
        isCheckbox: Boolean,
        radioName: String,
    },
    data() {
        return {
            isDropdownOpen: false,
            searchQuery: "",
            ico_dropdown_arrow: window.ico_dropdown_arrow,
            ico_search: window.ico_search,
            debouncedSearch: null,
            lastSelection: null, // Add this to track the last selection
        };
    },
    created() {
        this.debouncedSearch = debounce(this.performSearch, 300);
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('keydown', this.handleClickOutside);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('keydown', this.handleClickOutside);
        
    },
    computed: {
        filteredOptions() {
            if (!this.hasSearch || this.searchQuery.trim() === '') {
                return this.options;
            }
            const lowercaseQuery = this.searchQuery.toLowerCase();
            return this.options.filter((option) =>
                option.name.toLowerCase().includes(lowercaseQuery)
            );
        },
        selectedValueDisplay() {
            if (this.isCheckbox) {
                // Ensure modelValue is always treated as an array
                return Array.isArray(this.modelValue) ? this.modelValue.length : 
                       (this.modelValue ? [this.modelValue].length : 0);
            }
        },
        hasSelections() {
            return this.isCheckbox ? this.modelValue.length > 0 : !!this.modelValue;
        },
    },
    methods: {
        toggleDropdown() {
            if (!this.isDropdownOpen) {
                // Store the current selection when opening
                this.lastSelection = this.isCheckbox ? [...this.modelValue] : this.modelValue;
            } else {
                // When closing, check if selection changed
                const hasChanged = this.isCheckbox 
                    ? JSON.stringify(this.lastSelection) !== JSON.stringify(this.modelValue)
                    : this.lastSelection !== this.modelValue;
                
                if (hasChanged) {
                    this.applyFilters();
                }
            }
            this.isDropdownOpen = !this.isDropdownOpen;
            
            if (this.isDropdownOpen) {
                this.$nextTick(() => {
                    if (this.$refs.dropdownContent) {
                        const searchInput = this.$refs.dropdownContent.querySelector('input[type="text"]');
                        if (searchInput) {
                            searchInput.focus();
                        }
                    }
                });
            }
        },
        updateModelValue(value, checked) {
            if (this.isCheckbox) {
                // Ensure we're working with an array
                let newValue = Array.isArray(this.modelValue) ? [...this.modelValue] : 
                              (this.modelValue ? [this.modelValue] : []);
                
                if (checked) {
                    if (!newValue.includes(value)) newValue.push(value);
                } else {
                    // Remove the exact value, not split it
                    newValue = newValue.filter((item) => item !== value);
                }
                this.$emit("update:modelValue", newValue);
            } else {
                this.$emit("update:modelValue", value);
            }
        },
        applyFilters() {
            this.$emit("apply-filters");
            this.isDropdownOpen = false;
        },
        performSearch(value) {
            this.searchQuery = value;
        },
        handleClickOutside(event) {
            if (this.$refs.dropdownButton && !this.$refs.dropdownButton.contains(event.target) &&
                this.$refs.dropdownContent && !this.$refs.dropdownContent.contains(event.target)) {
                // Check if selection changed before closing
                const hasChanged = this.isCheckbox 
                    ? JSON.stringify(this.lastSelection) !== JSON.stringify(this.modelValue)
                    : this.lastSelection !== this.modelValue;
                
                this.isDropdownOpen = false;
                
                if (hasChanged) {
                    this.applyFilters();
                }
            }
        },
        printString(string) {
            return window.translations[string];
        },
    },
};
</script>

<style scoped>
.scroller {
    min-height: 80px;
    max-height: 300px; /* Adjust based on your needs */
    overflow-y: auto;
}

.vue-recycle-scroller__item-view:first-child {
   padding-top: 12px;                 
}

</style>
